import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import {
  getPendingLogs,
  hasPendingLogs,
  savePendingLog,
  clearPendingLogs,
  getScheduledMap,
  pruneScheduledMap,
} from './alarmLogStore';

async function detectFiredAlarms() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { notifications: pending } = await LocalNotifications.getPending();
    const pendingIds = pending.map(n => String(n.id));

    const before = getScheduledMap();
    const after = pruneScheduledMap(pendingIds);

    const beforeIds = new Set(Object.keys(before));
    const afterIds = new Set(Object.keys(after));

    // IDs that disappeared from the map → they fired
    for (const notifId of beforeIds) {
      if (!afterIds.has(notifId)) {
        const entry = before[notifId];
        const alarmId = entry.alarm_id;
        const existing = getPendingLogs()[String(alarmId)];

        if (!existing || existing.user_action_status == null) {
          savePendingLog(alarmId, {
            delivery_status: 'delivered',
            user_action_status: 'dismissed',
            fired_at: entry.fire_at,
            log_date: entry.fire_at ? entry.fire_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
          });
        }
      }
    }
  } catch (_) {}
}

async function flushPendingLogs(api, scholarId) {
  if (!hasPendingLogs()) return;

  const logs = Object.values(getPendingLogs());

  return new Promise((resolve) => {
    api.post('/alarm/log/delivery', { logs, scholar_id: scholarId }, (res) => {
      if (res.s) clearPendingLogs();
      resolve(res.s);
    });
  });
}

// Always calls the server so last_sync_time is updated on every visit.
async function syncScheduled(api, alarmIds, scholarId) {
  return new Promise((resolve) => {
    api.post('/alarm/log/sync', { alarm_ids: alarmIds || [], scholar_id: scholarId }, (res) => resolve(res.s));
  });
}

// Call this immediately when the app comes to foreground — before the alarm list loads.
// Detects any alarms that fired while the app was closed/backgrounded,
// saves them to localStorage, then flushes everything to the server and clears localStorage.
export async function flushLogsOnForeground(api, scholarId) {
  await detectFiredAlarms();
  await flushPendingLogs(api, scholarId);
}

// Call this after the alarm list has loaded.
// Always hits the server so last_sync_time is updated, even when alarms is empty.
export async function syncAlarmLogsOnOpen(api, alarms, scholarId) {
  const alarmIds = (alarms || []).map(a => a.alarm_id ?? a.id);

  await syncScheduled(api, alarmIds, scholarId);
}
